/**
 * Campaign Save Service - Single Source of Truth
 * 
 * This service handles all campaign saving operations.
 * It collects data from localStorage, validates it, and saves to Supabase.
 * 
 * IMPORTANT: This is the ONLY place campaigns should be saved from.
 */

import { supabase } from '@/integrations/supabase/client';
import { locations as allLocations } from '@/data/locations';
import { secureStorage } from '@/utils/secureStorage';
import { getScheduledDaysForUrl } from '@/utils/creativeScheduleStorage';
import type { Json } from '@/integrations/supabase/types';

// ==================== TYPES ====================

export interface CreativeInfo {
  url: string;
  locationName: string;
  mediaOwner: string;
  orientation: 'portrait' | 'landscape';
  resolution: string;
  scheduledDays: number[];
  originalFileName?: string;
}

export interface LocationInfo {
  id?: number;
  name: string;
  mediaOwner: string;
  region?: string;
  devices: number;
  pricePerDevice?: number;
  screenDimensions?: string | {
    width: number;
    height: number;
  };
  orientation?: string;
  details?: {
    screenDimensions?: string;
    description?: string;
    referenceImage?: string;
  };
}

export interface CampaignData {
  // Identity
  campaignId: string;
  campaignName: string;
  
  // Company Info
  companyName: string;
  brandName: string;
  personInCharge: string;
  jobTitle: string;
  email: string;
  mobile: string;
  
  // Campaign Details
  startDate: string;
  endDate: string;
  durationDays: number;
  totalDevices: number;
  
  // Locations
  selectedLocations: LocationInfo[];
  mediaOwnerName: string;
  locationName: string;
  
  // Budget
  totalBudget: number;
  budgetInfo: {
    subtotal?: number;
    vat?: number;
    total?: number;
    pricePerDevice?: number;
    [key: string]: any;
  };
  
  // Creatives
  creativeUrls: string[];
  creativeMetadata: CreativeInfo[];
  
  // Status
  status: string;
  paymentType: string;
  termsAccepted: boolean;
  
  // User
  userId?: string;
  creatorEmail?: string;
}

export interface SaveResult {
  success: boolean;
  campaignId?: string;
  error?: string;
}

// ==================== DATA COLLECTION ====================

/**
 * Safely parse JSON from localStorage
 */
function safeJsonParse<T>(key: string, defaultValue: T): T {
  try {
    const value = localStorage.getItem(key);
    if (!value || value === 'undefined' || value === 'null') {
      return defaultValue;
    }
    return JSON.parse(value) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * Format date to YYYY-MM-DD string
 */
function formatDate(dateInput: string | Date | null | undefined): string {
  if (!dateInput) {
    return new Date().toISOString().split('T')[0];
  }
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) {
      return new Date().toISOString().split('T')[0];
    }
    return date.toISOString().split('T')[0];
  } catch {
    return new Date().toISOString().split('T')[0];
  }
}

/**
 * Determine orientation from dimensions or URL
 */
function determineOrientation(width?: number, height?: number, url?: string): 'portrait' | 'landscape' {
  // From dimensions
  if (width && height) {
    return height > width ? 'portrait' : 'landscape';
  }
  
  // From URL patterns
  if (url) {
    if (url.includes('-portrait-') || url.includes('_portrait_')) return 'portrait';
    if (url.includes('-landscape-') || url.includes('_landscape_')) return 'landscape';
    
    // Check for dimension patterns in URL
    const dimMatch = url.match(/(\d{3,4})x(\d{3,4})/);
    if (dimMatch) {
      const w = parseInt(dimMatch[1]);
      const h = parseInt(dimMatch[2]);
      return h > w ? 'portrait' : 'landscape';
    }
  }
  
  return 'landscape'; // default
}

/**
 * Normalize a URL for comparison - handles encoding differences
 */
function normalizeUrlForMatch(url: string): string {
  if (!url) return '';
  try {
    let decoded = decodeURIComponent(url);
    decoded = decoded.split('?')[0].split('#')[0];
    return decoded;
  } catch {
    return url.split('?')[0].split('#')[0];
  }
}

/**
 * Extract filename from URL for matching
 */
function extractFilenameFromUrl(url: string): string {
  if (!url) return '';
  try {
    const decoded = decodeURIComponent(url);
    const path = decoded.split('?')[0].split('#')[0];
    return (path.split('/').pop() || '').toLowerCase();
  } catch {
    const path = url.split('?')[0].split('#')[0];
    return (path.split('/').pop() || '').toLowerCase();
  }
}

/**
 * Extract scheduled days from URL pattern (e.g., -day1-, -day2-, etc.)
 */
function extractScheduledDays(url: string): number[] {
  const days: number[] = [];
  const matches = url.matchAll(/-day(\d+)-/gi);
  for (const match of matches) {
    days.push(parseInt(match[1]));
  }
  return days.length > 0 ? days : [];
}

/**
 * Extract location name from creative URL
 */
function extractLocationFromUrl(url: string): string {
  try {
    // Pattern: timestamp-LocationName-filename
    const filename = url.split('/').pop() || '';
    const parts = filename.split('-');
    
    // Skip timestamp (first part) and look for location name
    if (parts.length >= 3) {
      // Try to find known location patterns
      const possibleLocation = parts.slice(1, -1).join(' ').replace(/_/g, ' ');
      if (possibleLocation.length > 2 && possibleLocation.length < 50) {
        return possibleLocation;
      }
    }
  } catch {}
  
  return 'Unknown';
}

/**
 * Parse screen dimensions from string like "4m x 7m" or "1920x1080"
 */
function parseScreenDimensions(dimStr?: string): { width: number; height: number } | null {
  if (!dimStr) return null;
  
  // Try "NNNxNNN" format (pixels)
  const pixelMatch = dimStr.match(/(\d+)\s*x\s*(\d+)/i);
  if (pixelMatch) {
    return { width: parseInt(pixelMatch[1]), height: parseInt(pixelMatch[2]) };
  }
  
  // Try "Nm x Nm" format (meters) - convert to approximate pixels (1m ≈ 100px for display)
  const meterMatch = dimStr.match(/(\d+(?:\.\d+)?)\s*m?\s*x\s*(\d+(?:\.\d+)?)\s*m?/i);
  if (meterMatch) {
    const w = parseFloat(meterMatch[1]) * 100;
    const h = parseFloat(meterMatch[2]) * 100;
    return { width: Math.round(w), height: Math.round(h) };
  }
  
  return null;
}

/**
 * Build creative metadata from all available sources
 * Prioritizes data from creativeDataMap (which has actual user-selected scheduled days)
 */
function buildCreativeMetadataWithStoredData(
  creativeUrls: string[],
  selectedLocations: LocationInfo[],
  storedMetadata: any[],
  durationDays: number,
  creativeDataMap: Map<string, { scheduledDays: number[]; locationName: string; orientation?: string }>
): CreativeInfo[] {
  const metadata: CreativeInfo[] = [];
  
  // Generate default scheduled days (all days of campaign) - only used if no days were set
  const defaultScheduledDays = Array.from({ length: durationDays }, (_, i) => i + 1);
  
  for (const url of creativeUrls) {
    // PRIORITY 1: Check the creativeDataMap with multiple matching strategies
    let storedCreativeData = creativeDataMap.get(url);
    if (!storedCreativeData) {
      // Try normalized URL
      storedCreativeData = creativeDataMap.get(normalizeUrlForMatch(url));
    }
    if (!storedCreativeData) {
      // Try filename match
      const filename = extractFilenameFromUrl(url);
      for (const [key, value] of creativeDataMap.entries()) {
        if (extractFilenameFromUrl(key) === filename) {
          storedCreativeData = value;
          break;
        }
      }
    }
    
    // PRIORITY 2: Check existing metadata with normalized matching
    let existing = storedMetadata?.find((m: any) => m.url === url);
    if (!existing) {
      existing = storedMetadata?.find((m: any) => normalizeUrlForMatch(m.url) === normalizeUrlForMatch(url));
    }
    if (!existing) {
      const filename = extractFilenameFromUrl(url);
      existing = storedMetadata?.find((m: any) => extractFilenameFromUrl(m.url) === filename);
    }
    
    // Try to find matching location
    const locationFromUrl = extractLocationFromUrl(url);
    const locationName = storedCreativeData?.locationName || existing?.locationName || locationFromUrl;
    
    const matchedLocation = selectedLocations.find(loc => 
      url.toLowerCase().includes(loc.name.toLowerCase().replace(/\s+/g, '').replace(/-/g, '')) ||
      loc.name.toLowerCase().includes(locationName?.toLowerCase() || '')
    );
    
    // Get screen dimensions from location details
    const screenDimStr = matchedLocation?.details?.screenDimensions || matchedLocation?.screenDimensions;
    const parsedDims = parseScreenDimensions(typeof screenDimStr === 'string' ? screenDimStr : undefined);
    
    // Determine orientation - priority: storedCreativeData > existing > location > URL
    let orientation: 'portrait' | 'landscape' = 'landscape';
    if (storedCreativeData?.orientation) {
      orientation = storedCreativeData.orientation as 'portrait' | 'landscape';
    } else if (existing?.orientation) {
      orientation = existing.orientation;
    } else if (parsedDims) {
      orientation = determineOrientation(parsedDims.width, parsedDims.height);
    } else {
      orientation = determineOrientation(undefined, undefined, url);
    }
    
    // Determine resolution
    let resolution = 'Unknown';
    if (existing?.resolution && existing.resolution !== 'Unknown') {
      resolution = existing.resolution;
    } else if (parsedDims) {
      resolution = `${parsedDims.width}x${parsedDims.height}`;
    } else if (typeof screenDimStr === 'string' && screenDimStr) {
      resolution = screenDimStr;
    } else {
      const dimMatch = url.match(/(\d{3,4})x(\d{3,4})/);
      if (dimMatch) {
        resolution = `${dimMatch[1]}x${dimMatch[2]}`;
      }
    }
    
    // Get scheduled days - PRIORITY:
    // 1) storedCreativeData (user's actual selection from localStorage state)
    // 2) existing (metadata persisted earlier)
    // 3) getScheduledDaysForUrl(url) (dedicated schedule storage; most reliable)
    // 4) URL embedded days
    // 5) empty (means user didn't assign)
    let scheduledDays: number[] = [];
    if (storedCreativeData?.scheduledDays && storedCreativeData.scheduledDays.length > 0) {
      // User explicitly set these days - use them!
      scheduledDays = storedCreativeData.scheduledDays;
    } else if (existing?.scheduledDays?.length > 0) {
      scheduledDays = existing.scheduledDays;
    } else {
      const storedByUrl = getScheduledDaysForUrl(url);
      if (storedByUrl && storedByUrl.length > 0) {
        scheduledDays = storedByUrl;
      } else {
        const urlDays = extractScheduledDays(url);
        // If we're here with no days, keep it empty to indicate user hasn't selected
        scheduledDays = urlDays.length > 0 ? urlDays : [];
      }
    }
    
    metadata.push({
      url,
      locationName: locationName || 'Unknown',
      mediaOwner: existing?.mediaOwner || matchedLocation?.mediaOwner || 'Unknown',
      orientation,
      resolution,
      scheduledDays,
      originalFileName: url.split('/').pop()?.split('-').pop() || undefined
    });
  }
  
  return metadata;
}

/**
 * Build creative metadata from all available sources (legacy function for backwards compatibility)
 */
function buildCreativeMetadata(
  creativeUrls: string[],
  selectedLocations: LocationInfo[],
  storedMetadata: any[],
  durationDays: number
): CreativeInfo[] {
  // Create empty map and delegate to the new function
  return buildCreativeMetadataWithStoredData(
    creativeUrls, 
    selectedLocations, 
    storedMetadata, 
    durationDays, 
    new Map()
  );
}

/**
 * Collect ALL campaign data from localStorage
 * This is the single source of truth for gathering campaign data
 */
export function collectCampaignData(overrides?: Partial<CampaignData>): CampaignData {
  // Form data
  const formData = safeJsonParse<Record<string, any>>('formData', {});
  
  // Locations
  const selectedLocations = safeJsonParse<LocationInfo[]>('selectedLocations', []);
  
  // Calculate total devices
  const totalDevices = selectedLocations.reduce((sum, loc) => sum + (loc.devices || 0), 0);
  
  // Media owner name(s)
  const mediaOwners = [...new Set(selectedLocations.map(l => l.mediaOwner).filter(Boolean))];
  const mediaOwnerName = mediaOwners.length === 1 
    ? mediaOwners[0] 
    : mediaOwners.length > 1 
      ? mediaOwners.join(', ')
      : 'Unknown';
  
  // Location name(s)
  const locationName = selectedLocations.length === 1
    ? selectedLocations[0].name
    : selectedLocations.length > 1
      ? 'Multiple Locations'
      : 'Unknown';
  
  // Dates
  const startDate = formatDate(localStorage.getItem('startDate'));
  const endDate = formatDate(localStorage.getItem('endDate'));
  const durationDays = parseInt(localStorage.getItem('duration') || '7') || 7;
  
  // Budget - try secureStorage first, then localStorage
  let budgetCalcs = secureStorage.get<Record<string, any>>('budgetCalculations') || {};
  if (!budgetCalcs.totalBudget && !budgetCalcs.totalCost) {
    budgetCalcs = safeJsonParse<Record<string, any>>('budgetCalculations', {});
  }
  const storedBudget = parseFloat(localStorage.getItem('totalBudget') || '0');
  const totalBudget = budgetCalcs.totalBudget || budgetCalcs.totalCost || budgetCalcs.total || storedBudget || 0;
  
  // Creative URLs - collect from all possible sources WITH their metadata
  const allCreativeUrls: string[] = [];
  const creativeDataMap: Map<string, { scheduledDays: number[]; locationName: string; orientation?: string }> = new Map();
  
  // Check if overrides contain raw creative data (from React state - most up-to-date)
  const overrideMediaOwnerCreatives = (overrides as any)?.rawMediaOwnerCreatives as Record<string, any[]> | undefined;
  
  // Main creatives (can be strings OR objects)
  const creativesRaw = safeJsonParse<any[]>('creatives', []);
  creativesRaw.forEach((c: any) => {
    const url = typeof c === 'string' ? c : c?.url;
    if (!url) return;

    allCreativeUrls.push(url);

    const rawDays = Array.isArray(c?.scheduledDays) ? c.scheduledDays : [];
    const days = rawDays.length > 0 ? rawDays : (getScheduledDaysForUrl(url) ?? []);

    // Keep a record for later matching
    creativeDataMap.set(url, {
      scheduledDays: days,
      locationName: c?.locationName || extractLocationFromUrl(url),
      orientation: c?.orientation,
    });
  });
  
  // All creative URLs storage
  const allCreatives = safeJsonParse<string[]>('allCreativeUrls', []);
  allCreativeUrls.push(...allCreatives);
  
  // Location-specific creatives - extract full Creative objects to get scheduledDays
  const locationCreatives = safeJsonParse<Record<string, any[]>>('locationCreatives', {});
  console.log('[CampaignSave] locationCreatives from localStorage:', JSON.stringify(locationCreatives));
  Object.entries(locationCreatives).forEach(([locationName, creativesArr]) => {
    creativesArr.forEach((creative: any) => {
      if (creative.url) {
        allCreativeUrls.push(creative.url);
        const rawDays = Array.isArray(creative.scheduledDays) ? creative.scheduledDays : [];
        const days = rawDays.length > 0 ? rawDays : (getScheduledDaysForUrl(creative.url) ?? []);
        console.log(`[CampaignSave] Location creative ${creative.url.substring(0, 50)}: scheduledDays=${JSON.stringify(days)}`);
        creativeDataMap.set(creative.url, {
          scheduledDays: days,
          locationName: creative.locationName || locationName,
          orientation: creative.orientation,
        });
      }
    });
  });
  
  // Media owner creatives - PRIORITIZE overrides (React state) over localStorage
  const mediaOwnerCreatives = overrideMediaOwnerCreatives || safeJsonParse<Record<string, any[]>>('mediaOwnerCreatives', {});
  console.log('[CampaignSave] mediaOwnerCreatives (source: ' + (overrideMediaOwnerCreatives ? 'React state' : 'localStorage') + '):', JSON.stringify(mediaOwnerCreatives));
  Object.entries(mediaOwnerCreatives).forEach(([locationName, creativesArr]) => {
    creativesArr.forEach((creative: any) => {
      if (creative.url) {
        allCreativeUrls.push(creative.url);
        const rawDays = Array.isArray(creative.scheduledDays) ? creative.scheduledDays : [];
        const days = rawDays.length > 0 ? rawDays : (getScheduledDaysForUrl(creative.url) ?? []);
        console.log(`[CampaignSave] MediaOwner creative ${creative.url.substring(0, 50)}: scheduledDays=${JSON.stringify(days)}`);
        creativeDataMap.set(creative.url, {
          scheduledDays: days,
          locationName: creative.locationName || locationName,
          orientation: creative.orientation,
        });
      }
    });
  });
  
  // Deduplicate
  const uniqueCreativeUrls = [...new Set(allCreativeUrls.filter(url => url && url.startsWith('http')))];
  
  // Creative metadata - pass the creativeDataMap to preserve scheduled days
  const storedMetadata = safeJsonParse<any[]>('creativeMetadata', []);
  const creativeMetadata = buildCreativeMetadataWithStoredData(
    uniqueCreativeUrls, 
    selectedLocations, 
    storedMetadata, 
    durationDays,
    creativeDataMap
  );
  
  // Campaign ID - use existing or generate new
  let campaignId = overrides?.campaignId || localStorage.getItem('currentCampaignId') || '';
  if (!campaignId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(campaignId)) {
    campaignId = crypto.randomUUID();
  }
  
  // Campaign name
  const campaignName = localStorage.getItem('campaignName') 
    || formData.brandName 
    || formData.companyName 
    || `Campaign ${new Date().toISOString().split('T')[0]}`;
  
  return {
    // Identity
    campaignId,
    campaignName,
    
    // Company Info
    companyName: formData.companyName || '',
    brandName: formData.brandName || '',
    personInCharge: formData.personInCharge || '',
    jobTitle: formData.jobTitle || '',
    email: formData.email || '',
    mobile: formData.mobile || '',
    
    // Campaign Details
    startDate,
    endDate,
    durationDays,
    totalDevices,
    
    // Locations
    selectedLocations,
    mediaOwnerName,
    locationName,
    
    // Budget
    totalBudget,
    budgetInfo: {
      subtotal: budgetCalcs.subtotal || totalBudget,
      vat: budgetCalcs.vat || 0,
      total: totalBudget,
      pricePerDevice: budgetCalcs.pricePerDevice || 0,
      ...budgetCalcs
    },
    
    // Creatives
    creativeUrls: uniqueCreativeUrls,
    creativeMetadata,
    
    // Status
    status: overrides?.status || 'pending',
    paymentType: localStorage.getItem('paymentType') || 'immediate',
    termsAccepted: formData.termsAccepted || localStorage.getItem('termsAccepted') === 'true',
    
    // User
    userId: overrides?.userId,
    creatorEmail: overrides?.creatorEmail || formData.email,
    
    // Apply any overrides
    ...overrides
  };
}

// ==================== SAVING ====================

/**
 * Save campaign via Edge Function
 * This is the ONLY function that should save campaigns - uses server-side validation
 */
export async function saveCampaign(data: CampaignData): Promise<SaveResult> {
  // Check session storage for this session (skip duplicate saves)
  const sessionKey = `campaign_saved_${data.campaignId}`;
  if (sessionStorage.getItem(sessionKey) === 'true') {
    console.log('[CampaignSave] Campaign already saved this session:', data.campaignId);
    return { success: true, campaignId: data.campaignId };
  }
  
  try {
    console.log('[CampaignSave] Starting save via edge function for campaign:', data.campaignId);
    console.log('[CampaignSave] Data summary:', {
      name: data.campaignName,
      locations: data.selectedLocations.length,
      creatives: data.creativeUrls.length,
      budget: data.totalBudget
    });
    
    // Log creative metadata for debugging scheduled days
    console.log('[CampaignSave] Creative metadata being sent:');
    data.creativeMetadata.forEach((cm, i) => {
      console.log(`  [${i}] url: ${cm.url.substring(0, 50)}..., scheduledDays: ${JSON.stringify(cm.scheduledDays)}, location: ${cm.locationName}`);
    });

    // Build dedicated creative schedules section (explicit per-creative reference)
    const creativeSchedules = data.creativeMetadata
      .map((cm, i) => ({
        creativeId: `creative_${i}`,
        creativeUrl: cm.url,
        locationName: cm.locationName,
        scheduledDays: Array.isArray(cm.scheduledDays) ? cm.scheduledDays : [],
        createdAt: new Date().toISOString(),
      }))
      .filter((cs) => cs.scheduledDays.length > 0);
    
    // Prepare payload for edge function
    const payload = {
      campaignId: data.campaignId,
      campaignName: data.campaignName,
      companyName: data.companyName,
      brandName: data.brandName,
      personInCharge: data.personInCharge,
      jobTitle: data.jobTitle,
      email: data.email,
      mobile: data.mobile,
      selectedLocations: data.selectedLocations,
      creativeUrls: data.creativeUrls,
      creativeMetadata: data.creativeMetadata,
      creativeSchedules,
      totalDevices: data.totalDevices,
      durationDays: data.durationDays,
      startDate: data.startDate,
      endDate: data.endDate,
      totalBudget: data.totalBudget,
      budgetInfo: data.budgetInfo,
      termsAccepted: data.termsAccepted,
      paymentType: data.paymentType,
      status: data.status,
    };
    
    console.log('[CampaignSave] Calling create-campaign edge function');
    
    // Call the edge function
    const { data: result, error } = await supabase.functions.invoke('create-campaign', {
      body: payload,
    });
    
    if (error) {
      console.error('[CampaignSave] Edge function error:', error);
      return { success: false, error: error.message };
    }
    
    if (!result?.success) {
      console.error('[CampaignSave] Edge function returned failure:', result);
      return { success: false, error: result?.error || 'Unknown error from edge function' };
    }
    
    console.log('[CampaignSave] Campaign saved successfully via edge function:', result.campaignId);
    sessionStorage.setItem(sessionKey, 'true');
    
    // Set flag for dashboard refresh
    localStorage.setItem('campaignCreated', 'true');
    localStorage.setItem('lastCampaignId', result.campaignId || data.campaignId);
    
    return { success: true, campaignId: result.campaignId || data.campaignId };
    
  } catch (error) {
    console.error('[CampaignSave] Exception:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Main entry point - collect data and save
 */
export async function saveCurrentCampaign(overrides?: Partial<CampaignData>): Promise<SaveResult> {
  // Add a small delay to ensure any pending localStorage writes complete
  // This handles race conditions between React state updates and localStorage persistence
  await new Promise(resolve => setTimeout(resolve, 50));
  
  const data = collectCampaignData(overrides);
  return saveCampaign(data);
}
